<script setup lang="ts">
import type { BreadcrumbType } from '#build/types/global'
import { useCoursesStore } from '~~/stores/courses'

defineOptions({
  name: 'KategoriContent'
})
const { isMobile } = useDevice()
const route = useRoute()
const coursesStore: Record<string, any> = useCoursesStore()

const courseList = computed(() => coursesStore.state?.courseList)

const title = computed(() => {
  const hasSerial = route.params.serial
  const category = courseList?.value?.data?.[0]?.category
  return hasSerial ? category : 'Semua kategori'
})

const breadcrumb = computed<BreadcrumbType[]>(() => {
  const hasSerial = route.params.serial
  const data = [
    {
      path: '/',
      active: false,
      label: 'Beranda'
    },
    {
      path: hasSerial ? '/kategori' : '',
      active: !hasSerial,
      label: 'Kategori'
    }
  ]

  if (hasSerial) {
    data.push({
      path: '',
      active: true,
      label: title.value
    })
  }
  return data
})

const fetchList = (page: number) => {
  coursesStore.fetchCourseList({
    payload: {
      categorySerial: route.params?.serial,
      page,
      size: 12
    }
  })
}

const activeIndex = ref(null)

const {
  triggerReview,
  showReview,
  onCancelReview,
  triggerFinishRatingCourse,
  triggerFinishRatingFacilitator,
  submitAddReview,
  visibleAddRating
} = useGenerateCertificate()

const activeCourseIndex = ({ index, course }: Record<string, any>) => {
  activeIndex.value = index
  triggerReview(course)
}

const finisihAddRating = () => {
  triggerFinishRatingCourse()
  coursesStore.state.courseList[activeIndex.value].isReviewed = true
}

const showFilter = ref(false)

const toggleFilter = () => {
  showFilter.value = !showFilter.value
}

fetchList(1)
</script>

<template>
  <section id="kategori-content" class="kategori-content">
    <a-row :gutter="[24, 24]">
      <a-col :span="24">
        <AtomsBreadcrumbs :breadcrumbs="breadcrumb" />
      </a-col>
      <a-col :span="24">
        <div class="kategori-content__header">
          <h1>{{ title }}</h1>
        </div>
      </a-col>
      <a-col v-if="isMobile" :span="24">
        <a-button
          class="kategory-content__trigger-filter"
          @click="toggleFilter"
        >
          Filter
          <template #icon>
            <FilterOutlined />
          </template>
        </a-button>

        <LazyAtomsMobileBottomContainer
          :show="showFilter"
          @close="showFilter = false"
        >
          <LazyModulesKategoriMoleculesAside />
        </LazyAtomsMobileBottomContainer>
      </a-col>
      <a-col :span="24">
        <a-row :gutter="[32, 32]">
          <template v-if="courseList?.isLoading">
            <a-col v-for="index in 6" :key="index" :xs="24" :md="12" :lg="12">
              <LazyAtomsCardCourseLoading />
            </a-col>
          </template>
          <template
            v-else-if="!courseList?.isLoading && courseList.data?.length"
          >
            <a-col
              v-for="(course, index) in courseList.data"
              :key="course.serial"
              :lg="12"
              :md="12"
              :xs="24"
            >
              <LazyModulesHomeMoleculesCardCourse
                v-bind="course"
                @on-review="activeCourseIndex({ index, course })"
              />
            </a-col>
          </template>
          <a-col v-else :span="24">
            <AtomsEmpty height="200px" description="Kategori tidak ditemukan" />
          </a-col>
        </a-row>
      </a-col>
      <a-col v-if="!isEmpty(courseList.data)" :span="24">
        <div class="block w-full text-center">
          <a-pagination
            :current="courseList?.meta?.page"
            size="small"
            :total="courseList?.meta?.total"
            :page-size="12"
            :show-size-changer="false"
            @change="fetchList"
          />
        </div>
      </a-col>
    </a-row>

    <ClientOnly>
      <ModulesStudiSayaAtomsModalReviewInfo
        :show-review="showReview"
        @submit="submitAddReview"
        @cancel="onCancelReview"
      />
      <AtomsModal
        :visible="visibleAddRating.course"
        :width="1073"
        centered
        :closable="false"
      >
        <LazyModulesKursusCertificateAtomsRating
          title="Tulis Review"
          @on-finish="finisihAddRating"
        />
      </AtomsModal>

      <AtomsModal
        :visible="visibleAddRating.facilitator"
        :width="676"
        centered
        @cancel="triggerFinishRatingFacilitator"
      >
        <LazyModulesKursusCertificateAtomsRatingFacilitator
          @on-finish="triggerFinishRatingFacilitator"
        />
      </AtomsModal>
    </ClientOnly>
  </section>
</template>

<style lang="scss">
@use './styles.scss';
</style>
